import { IconChevronDown, IconChevronUp } from "@/assets/icons";
import { IconGoogle } from "@/assets/icons/IconGoogle";
import { IconLogin } from "@/assets/icons/IconLogin";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { loginSchema } from "@/features/auth/schema/loginSchema";
import { loginApi } from "@/features/auth/services/loginApi";
import { TLoginRequest } from "@/features/auth/types/loginRequest";
import { buildCognitoGoogleLoginUrl } from "@/features/auth/utils/buildCognitoGoogleLoginUrl";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { setErrorForm, showError } from "@/utils/error";
import { logout } from "@/utils/logout";
import { getQueryFromURL, getSafeRedirectURL } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Collapse, Paper, Stack, Typography, styled } from "@mui/material";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

type TQuery = {
  email: string;
  redirectUrl: string;
};

export const LoginForm = () => {
  const navigate = useNavigate();
  const query = getQueryFromURL<TQuery>();
  const [showEmailLogin, setShowEmailLogin] = useState(false);

  const defaultValues = useMemo(() => {
    if (query && query.email) return { email: query.email };
  }, [query]);

  const methods = useForm<TLoginRequest>({
    defaultValues,
    mode: "all",
    reValidateMode: "onBlur",
    resolver: yupResolver(loginSchema),
  });
  const { control, formState } = methods;

  const onSubmit = async (data: TLoginRequest) => {
    try {
      const response = await loginApi(data);
      await logout();
      navigate(AppRoutes.loginVerify, {
        state: {
          email: response.email,
          pendingAuthToken: response.pendingAuthToken,
          redirectUrl: getSafeRedirectURL(query?.redirectUrl),
        },
      });
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = buildCognitoGoogleLoginUrl();
  };

  const isFormValid = !formState.isSubmitting;

  return (
    <Container
      elevation={0}
      sx={{
        width: 464,
        p: 4,
        borderRadius: "16px",
      }}
    >
      <Stack gap={2}>
        <GoogleButton fullWidth onClick={handleGoogleLogin}>
          <IconGoogle />
          Google Workspace でログイン
        </GoogleButton>
        <Box display="flex" alignItems="center" gap={2} my={2}>
          <Box flex={1} height="1px" bgcolor="#E0E0E0" />
          <Typography color={text.secondary} fontSize="14px">
            または
          </Typography>
          <Box flex={1} height="1px" bgcolor="#E0E0E0" />
        </Box>
        <CollapsibleHeader onClick={() => setShowEmailLogin(!showEmailLogin)}>
          <Typography color={text.secondary} fontSize="14px" fontWeight={500}>
            メールアドレスでログイン
          </Typography>
          {showEmailLogin ? (
            <IconChevronUp style={{ color: text.secondary, fontSize: "18px" }} />
          ) : (
            <IconChevronDown style={{ color: text.secondary, fontSize: "18px" }} />
          )}
        </CollapsibleHeader>
        <Stack>
          <Collapse in={showEmailLogin}>
            <Form methods={methods} onSubmit={onSubmit} enableBlocker={false} schema={loginSchema}>
              <Stack gap={2.5}>
                <FormField control={control} name="email" label="メールアドレス">
                  <Input size="lg" placeholder="example@irohana.co.jp" />
                </FormField>
                <FormField control={control} name="password" label="パスワード">
                  <Input type="password" size="lg" placeholder="パスワードを入力" />
                </FormField>
                <Button
                  type="submit"
                  variant="outline"
                  fullWidth
                  startIcon={<IconLogin color="currentColor" />}
                  disabled={!isFormValid}
                  sx={{
                    mt: 1,
                    height: "48px !important",
                    borderRadius: "8px",
                    "&.Mui-disabled": {
                      backgroundColor: "#E0E0E0",
                      color: "#9E9E9E",
                    },
                  }}
                >
                  <Typography variant="body14Semi">ログイン</Typography>
                </Button>
              </Stack>
            </Form>
          </Collapse>
          <Box display="flex" justifyContent="center" mt={showEmailLogin ? 2 : 0}>
            <Button
              variant="ghost"
              sx={{
                width: "100%",
              }}
              size="sm"
              component={Link}
              to={AppRoutes.recover}
            >
              パスワードを忘れた方はこちら
            </Button>
          </Box>
        </Stack>
      </Stack>
    </Container>
  );
};

const Container = styled(Paper)`
  box-shadow:
    0 4px 4px -1px rgba(12, 12, 13, 0.05),
    0 4px 4px -1px rgba(12, 12, 13, 0.1) !important;
`;

const GoogleButton = styled(Button)`
  &.MuiButtonBase-root {
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    height: 60px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
  }
`;

const CollapsibleHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px;
  &:hover {
    opacity: 0.8;
  }
`;
